<template>
  <div id="map-container">
    <div class="echarts">
      <div id="map"></div>
    </div>
  </div>
</template>

<script>

import {getChinaJson, getProvinceJson, getCityJson, getDistrictJson} from "@/api/map";
import {mapOption} from '@/config/mapOption'
import resize from '@/utils/resize'


export default {
  mixins: [resize],
  name: "china",
  components: {},
  props: {
    areaCode: {
      type: String,
      default: '000000'
    },
    areaLevel: {
      type: [String, Number],
      default: 0
    },
    areaName: {
      type: String,
      default: 'china'
    },
    // 当前地图上的地区名字
    mapNameList: {
      type: Array,
      default() {
        return []
      }
    },
    // 当前地图上的地区Code
    mapCodeList: {
      type: Array,
      default() {
        return []
      }
    },
    // 地区统计数据
    areaStatistic: {
      type: Array,
      default() {
        return []
      }
    }
  },
  data() {
    return {
      chart: null, // 实例化echarts
      mapDataList: [], // 当前地图上的地区
      option: {...mapOption.basicOption}, // map的相关配置
      deepTree: [],// 点击地图时push，点返回时pop
      areaStatisticMapValue: {}, // 地图数据value, 只是amounts
      areaStatisticMapData: {}, // 地图数据data,包含所有数据
      areaLevelMap: {
        'country': 0,
        'china': 0,
        'province': 1,
        'city': 2,
        'district': 3,
      },
      tooltipAutoplay: null, // 提示框自动播放
      tooltipAutoplayIndex: 0, // 提示框自动播放index
      isReturning: false, // ✅ 标记是否正在执行返回操作，用于控制 areaStatistic 更新时的行为
    }
  },
  beforeDestroy() {
    // ✅ 修复：先清理定时器，再销毁图表
    clearInterval(this.tooltipAutoplay)
    
    if (!this.chart) {
      return
    }
    this.chart.dispose()
    this.chart = null
    
    // 移除监听器
    window.removeEventListener('orientationchange', this.handleOrientationChange)
  },
  mounted() {
    // ✅ 清空 deepTree，确保从头开始
    this.deepTree = [];
    
    this.$nextTick(() => {
      this.initEcharts();
      this.chart.on('click', this.echartsMapClick);
      this.chart.on('mouseover', this.echartsMapMouseover);
      this.chart.on('mouseout', this.echartsMapMouseout);
    });
    
    // 监听屏幕方向变化
    window.addEventListener('orientationchange', this.handleOrientationChange)
  },
  watch: {
    areaStatistic: {
      handler(val) {
        var objValue = {}, objData = {}
        for (var i = 0; i < val.length; i++) {
          // ✅ 确保 areaCode 转换为字符串后再使用 substr
          const areaCodeStr = String(val[i]['areaCode']);
          objValue[areaCodeStr.substr(0, 6)] = val[i].amounts * 1
          objData[areaCodeStr.substr(0, 6)] = val[i]
        }
        this.areaStatisticMapValue = objValue
        this.areaStatisticMapData = objData
        
        // ✅ 如果正在返回，需要先渲染地图再更新数据
        if (this.isReturning) {
          // ✅ 从 deepTree 中获取当前层级的信息
          const currentLevel = this.deepTree[this.deepTree.length - 1];
          const areaName = currentLevel.params.name;
          
          // ✅ 先渲染地图结构
          this.renderMap(areaName, this.mapDataList);
          
          // ✅ 然后更新数据
          this.$nextTick(() => {
            this.updateMapData();
            this.isReturning = false;
          });
          return
        }
        
        // ✅ 检查是否需要重新初始化地图
        const shouldReinit = !this.chart || !this.mapDataList || this.mapDataList.length === 0;
        
        if (shouldReinit) {
          this.initEcharts();
        } else {
          this.updateMapData();
        }
      },
      deep: true,
    }
  },
  methods: {
    handleOrientationChange() {
      // 屏幕方向变化后，延迟重新渲染地图
      setTimeout(() => {
        if (this.chart) {
          this.chart.resize()
        }
      }, 300)
    },
    // ✅ 手动更新地图数据（不重新初始化）
    updateMapData() {
      if (!this.chart || !this.mapDataList || this.mapDataList.length === 0) {
        return
      }
      
      // 更新 mapDataList 中的 value
      const updatedData = this.mapDataList.map(item => {
        const areaCodeKey = item.adcode ? String(item.adcode).substr(0, 6) : '';
        // ✅ 如果 areaStatisticMapValue 中有这个 key，使用新值；否则保持原值
        const hasNewData = this.areaStatisticMapValue.hasOwnProperty(areaCodeKey);
        const equipmentCount = hasNewData ? this.areaStatisticMapValue[areaCodeKey] : item.value;
        return {
          ...item,
          value: equipmentCount
        }
      })
      
      // 生成散点数据
      var pointData = []
      for (var i = 0; i < updatedData.length; i++) {
        if (updatedData[i].value > 0 && updatedData[i].center && Array.isArray(updatedData[i].center) && updatedData[i].center.length >= 2) {
          pointData.push({
            ...updatedData[i],
            value: [updatedData[i].center[0], updatedData[i].center[1], updatedData[i].value],
          })
        }
      }
      
      // ✅ 计算散点最大值（用于 symbolSize 函数）
      const scatterMaxValue = pointData.length > 0 ? Math.max(...pointData.map(p => p.value[2])) : 1;
      
      // ✅ 更新 visualMap 的范围
      const nonZeroValues = updatedData.filter(item => item.value > 0).map(item => item.value);
      
      if (nonZeroValues.length > 0) {
        this.option.visualMap.min = Math.min(...nonZeroValues);
        this.option.visualMap.max = Math.max(...nonZeroValues);
      } else {
        this.option.visualMap.min = 0;
        this.option.visualMap.max = 0;
      }
      
      // 重新设置 option 中的数据 - 包括地图和散点两个 series
      const newOption = {
        visualMap: this.option.visualMap,
        series: [
          {
            data: updatedData
          },
          {
            data: pointData,
            symbolSize: (function(maxValue) {
              return function (val) {
                if (!val || val.length < 3) return 6;
                
                const value = val[2];
                
                if (!maxValue || maxValue === 0) return 6;
                
                const ratio = value / maxValue;
                const size = 6 + (ratio * 14);
                
                return Math.max(6, Math.min(20, size));
              };
            })(scatterMaxValue)
          }
        ]
      }
      
      this.chart.setOption(newOption)
    },
    
    // 初次加载绘制地图
    initEcharts() {
      // ✅ 优化：避免重复初始化
      if (!this.chart) {
        //地图容器
        this.chart = this.$echarts.init(document.getElementById('map'), null, {renderer: 'canvas'});
      }
      
      if (this.areaLevel === 0) {
        this.requestGetChinaJson();
      } else if (this.areaLevel === 1) {
        // ✅ 确保 areaCode 为字符串类型
        const areaCodeStr = String(this.areaCode);
        this.requestGetProvinceJSON({name: this.areaName, level: 'province', adcode: areaCodeStr.substr(0, 6)})
      } else if (this.areaLevel === 2) {
        // ✅ 确保 areaCode 为字符串类型
        const areaCodeStr = String(this.areaCode);
        this.requestGetCityJSON({name: this.areaName, level: 'city', adcode: areaCodeStr.substr(0, 6)})
      } else if (this.areaLevel === 3) {
        // ✅ 确保 areaCode 为字符串类型
        const areaCodeStr = String(this.areaCode);
        this.requestGetDistrictJSON({name: this.areaName, level: 'district', adcode: areaCodeStr.substr(0, 6)})
      } else {
        return false
      }
    },
    // 地图点击
    echartsMapClick(params) {
      // ✅ 标准化 areaCode 为6位格式，与后端保持一致
      const rawAdcode = params.data.adcode;
      const normalizedAreaCode = rawAdcode ? String(rawAdcode).substring(0, 6) : '000000';
      
      this.$emit('update:areaCode', normalizedAreaCode)
      this.$emit('update:areaName', params.data.name)
      this.$emit('update:areaLevel', this.areaLevelMap[params.data.level])
      if (params.data.level === 'province') {
        this.requestGetProvinceJSON(params.data);
      } else if (params.data.level === 'city') {
        this.requestGetCityJSON(params.data)
      } else if (params.data.level === 'district' && this.mapDataList.length > 1) {
        this.requestGetDistrictJSON(params.data)
      } else {
        return false
      }
    },
    //绘制全国地图areaStatistic
    requestGetChinaJson() {
      getChinaJson().then(res => {
        // console.log('china--->', res)
        this.$emit('update:areaLevel', 0)
        this.setJsonData(res)
      });
    },
    // 加载省级地图
    requestGetProvinceJSON(params) {
      getProvinceJson(params.adcode).then(res => {
        // console.log('province--->', res)
        this.$emit('update:areaLevel', 1)
        this.setJsonData(res, params)
      });
    },
    // 加载市级地图
    requestGetCityJSON(params) {
      getCityJson(params.adcode).then(res => {
        // console.log('city--->', res)
        this.$emit('update:areaLevel', 2)
        this.setJsonData(res, params)
      })
    },
    // 加载县级地图
    requestGetDistrictJSON(params) {
      getDistrictJson(params.adcode).then(res => {
        // console.log('district--->', res)
        this.$emit('update:areaLevel', 3)
        this.setJsonData(res, params)
      })
    },
    // 设置数据
    setJsonData(res, params) {
      var mapDataList = [];
      var mapNameList = [];
      var mapCodeList = [];
      
      for (var i = 0; i < res.features.length; i++) {
        const adcode = res.features[i].properties.adcode;
        const areaCodeKey = adcode ? adcode.toString().substr(0, 6) : '';
        
        // 从 areaStatisticMapValue 中获取真实的装机数量
        const equipmentCount = this.areaStatisticMapValue[areaCodeKey] || 0;
        const statisticData = this.areaStatisticMapData[areaCodeKey] || {};
        
        var obj = {
          ...res.features[i].properties,
          value: equipmentCount,  // 使用真实数据
          valueData: statisticData,  // 保存完整统计数据
        };
        mapDataList.unshift(obj)
        mapNameList.unshift(res.features[i].properties.name)
        mapCodeList.unshift(adcode + '000000')
      }
      this.mapDataList = mapDataList;
      this.$emit('update:mapNameList', mapNameList)
      this.$emit('update:mapCodeList', mapCodeList)
      this.setMapData(res, params)
    },
    // 设置地图信息
    setMapData(res, params) {
      if (this.areaName === 'china') {
        this.deepTree.push({
          mapDataList: this.mapDataList,
          params: {name: 'china', level: 'country', adcode: '100000'}
        });
        //注册地图
        this.$echarts.registerMap('china', res);
        //绘制地图
        this.renderMap('china', this.mapDataList);
      } else {
        this.deepTree.push({mapDataList: this.mapDataList, params: params});
        this.$echarts.registerMap(params.name, res);
        this.renderMap(params.name, this.mapDataList);
      }
    },
    // 渲染地图
    renderMap(map, data) {
      var mapDataList = data.map(item => {
        return {
          name: item.name,
          value: item.value
        }
      })
      mapDataList = mapDataList.sort(function (a, b) {
        return b.value - a.value
      });
      var pointData = []
      var missingCenterRegions = [] // ✅ 记录缺少 center 的地区
      
      for (var i = 0; i < data.length; i++) {
        // ✅ 安全检查：确保 value > 0 且 center 存在
        if (data[i].value > 0 && data[i].center && Array.isArray(data[i].center) && data[i].center.length >= 2) {
          pointData.push({
            ...data[i],
            value: [data[i].center[0], data[i].center[1], data[i].value],
          })
        } else if (data[i].value > 0) {
          // ✅ 记录有数据但缺少 center 的地区（仅开发环境）
          if (process.env.NODE_ENV === 'development') {
            missingCenterRegions.push({
              name: data[i].name,
              value: data[i].value,
              hasCenter: !!data[i].center,
              centerType: Array.isArray(data[i].center) ? 'array' : typeof data[i].center
            })
          }
        }
      }
      
      // ✅ 设置左下角数量范围值
      const nonZeroValues = mapDataList.filter(item => item.value > 0).map(item => item.value);
      
      if (nonZeroValues.length > 0) {
        this.option.visualMap.min = Math.min(...nonZeroValues);
        this.option.visualMap.max = Math.max(...nonZeroValues);
      } else {
        this.option.visualMap.min = 0;
        this.option.visualMap.max = 0;
      }
      
      // ✅ 计算散点最大值（用于 symbolSize 函数）
      const scatterMaxValue = pointData.length > 0 ? Math.max(...pointData.map(p => p.value[2])) : 1;
      
      // 设置左上角当前位置
      this.option.title[0].text = map === 'china' ? '全国' : map
      this.option.geo = {
        show: false,
        map: map,
        zoom: 1.2, //当前视角的缩放比例
        roam: true, //是否开启平游或缩放
        center: undefined,
      }
      this.option.series = [
        {
          name: map,
          mapType: map,
          zoom: 1, //当前视角的缩放比例
          roam: false, //是否开启平游或缩放
          center: undefined,
          scaleLimit: { //滚轮缩放的极限控制
            min: .5,
            max: 10
          },
          ...mapOption.seriesOption,
          data: data,
          // 自定义 tooltip 显示
          label: {
            show: true,
            formatter: (params) => {
              // ✅ 应用地区名称简称转换
              let displayName = params.name;
              switch (params.name) {
                case '内蒙古自治区':
                  displayName = '内蒙古';
                  break;
                case '西藏自治区':
                  displayName = '西藏';
                  break;
                case '新疆维吾尔自治区':
                  displayName = '新疆';
                  break;
                case '宁夏回族自治区':
                  displayName = '宁夏';
                  break;
                case '广西壮族自治区':
                  displayName = '广西';
                  break;
                case '香港特别行政区':
                  displayName = '香港';
                  break;
                case '澳门特别行政区':
                  displayName = '澳门';
                  break;
              }
              
              const count = params.data?.value || 0;
              return `${displayName}\n${count}台`;
            },
            color: '#fff',
            fontSize: window.innerWidth >= 1920 && window.innerHeight >= 1080 ? 14 : 10, // ✅ 1080p大屏增大字体
          }
        },
        {
          name: '散点',//series名称
          type: 'effectScatter',// ✅ 优化：从 'effectScatter' 改为 'scatter'
          coordinateSystem: 'geo',// series坐标系类型
          // ✅ 优化：移除了 rippleEffect 配置
          rippleEffect: {
            period: 10,           // ✅ 优化：增加动画周期，从 4s 变为 6s，减少计算频率
            brushType: 'stroke', // ✅ 优化：改为描边模式，比 fill 性能好很多
            scale: 3             // ✅ 优化：限制最大扩散倍数
          },
          label: {
            show: false
          },
          emphasis: {
            label: {
              show: true,
              formatter: params => params.name,
              position: 'top',
              color: '#fff'
            }
          },
          itemStyle: {
            color: '#F4E925',
            shadowBlur: 10,
            shadowColor: '#000'
          },
          data: pointData,
          symbolSize: (function(maxValue) {
            // ✅ 使用闭包保存最大值
            return function (val) {
              if (!val || val.length < 3) return 6;
                      
              const value = val[2];
                             
              // 最小值 6，最大值 20，根据比例计算
              if (!maxValue || maxValue === 0) return 6;
                      
              const ratio = value / maxValue;
              const size = 6 + (ratio * 14); // 6 ~ 20
                        
              return Math.max(6, Math.min(20, size));
            };
          })(scatterMaxValue),
          showEffectOn: 'render', //加载完毕显示特效
        },
      ]
      
      // ✅ 检测是否为移动端，调整配置
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        // 移动端优化：调整 series 配置
        if (this.option.series && this.option.series.length > 0) {
          // 第一个 series 是地图
          if (this.option.series[0].label && this.option.series[0].label.normal) {
            this.option.series[0].label.normal.textStyle.fontSize = 8;
          }
          if (this.option.series[0].itemStyle && this.option.series[0].itemStyle.normal) {
            this.option.series[0].itemStyle.normal.borderWidth = 0.8;
          }
          this.option.series[0].zoom = 1;
          
          // 第二个 series 是散点（如果存在）
          if (this.option.series[1]) {
            this.option.series[1].symbolSize = (function(maxValue) {
              return function (val) {
                if (!val || val.length < 3) return 4;
                const value = val[2];
                if (!maxValue || maxValue === 0) return 4;
                const ratio = value / maxValue;
                const size = 4 + (ratio * 10); // 减小尺寸
                return Math.max(4, Math.min(14, size));
              };
            })(scatterMaxValue);
          }
        }
        
        // 调整 tooltip 字体
        if (this.option.tooltip && this.option.tooltip.textStyle) {
          this.option.tooltip.textStyle.fontSize = window.innerWidth >= 1920 && window.innerHeight >= 1080 ? 16 : 12; // ✅ 1080p大屏增大字体
        }
      }
      
      //渲染地图
      this.chart.setOption(this.option, true)
      this.setTooltipAutoplay()
    },
    // 地图鼠标移入事件
    echartsMapMouseover() {
      clearInterval(this.tooltipAutoplay)
    },
    // 地图鼠标移出事件
    echartsMapMouseout() {
      this.setTooltipAutoplay()
    },
    // 动态显示tooltip
    setTooltipAutoplay() {
      clearInterval(this.tooltipAutoplay)
      
      // ✅ 安全检查：确保 chart 存在
      if (!this.chart) {
        return
      }
      
      // ✅ 优化：如果没有数据或数据很少，不启动自动播放
      if (!this.mapDataList || this.mapDataList.length === 0) {
        return
      }
      
      this.tooltipAutoplay = setInterval(() => {
        // ✅ 修复：在定时器回调中也检查 chart 是否存在
        if (!this.chart) {
          clearInterval(this.tooltipAutoplay)
          return
        }
        
        this.chart.dispatchAction({
          type: 'showTip',
          seriesIndex: 0,
          dataIndex: this.tooltipAutoplayIndex
        })
        this.tooltipAutoplayIndex++
        if (this.tooltipAutoplayIndex >= this.mapDataList.length) {
          this.tooltipAutoplayIndex = 0;
          this.setTooltipAutoplay()
        }
      }, 6666) // ✅ 优化：增加间隔时间，从3秒增加到6.6秒
    },
    // 返回
    async back() {
      if (this.deepTree.length > 1) {
        // ✅ 设置标志，防止 areaStatistic 更新时重新初始化地图
        this.isReturning = true;
        
        this.deepTree.pop();
        const prevLevel = this.deepTree[this.deepTree.length - 1];
        this.mapDataList = prevLevel.mapDataList;
        var areaName = prevLevel.params.name;
        var areaCode = prevLevel.params.adcode;
        var mapNameList = this.mapDataList.map(item => {
          return item.name
        })
        
        // ✅ 确保 areaCode 为字符串类型
        const normalizedAreaCode = areaCode === '100000' ? '000000' : String(areaCode);
        
        // ✅ 先发射事件，触发父组件加载新数据
        this.$emit('update:areaCode', normalizedAreaCode)
        this.$emit('update:areaName', areaName)
        this.$emit('update:mapNameList', mapNameList)
        
        // ✅ 不在这里渲染地图，等待 watch areaStatistic 中处理
      }
    }
  }
}

</script>

<style lang="scss" scoped>
#map-container {
  height: 66.6%;
  position: relative;

  .echarts {
    height: 100%;

    #map {
      width: 100%;
      height: 100%;
    }
  }
}

// 平板端和手机横屏适配
@media screen and (min-width: 769px) {
  #map-container {
  }
}

// 手机横屏特殊处理
@media screen and (orientation: landscape) and (max-height: 768px) {
  #map-container {
    height: calc((100% - 30px) * 2 / 3) !important; // ✅ 占中间列的三分之二，考虑padding
    min-height: unset !important;
    margin-bottom: 20px !important; // 与概览数据之间留间距
  }
}

// 手机竖屏适配
@media screen and (max-width: 768px) and (orientation: portrait) {
  #map-container {
    height: 400px !important;
    min-height: 300px;
    
    .echarts {
      height: calc(100% - 50px) !important;
    }
  }
}

// ✅ 1080p 屏幕专属优化
@media screen and (min-width: 1920px) and (max-width: 2560px) and (min-height: 1080px) and (max-height: 1440px) {
  #map-container {
  }
}
</style>

